import { Component } from 'react';
import { useCookies } from 'react-cookie';
import { useAtom } from 'jotai';
import { Link } from 'react-router-dom';

import { APP_ROUTE } from '@/constants';
import ErrorCard from '@/components/error-card/new-error-card';
import { getErrorAction } from '@/views/dashboard/utils';
import { Button } from '@/components/ui/button';
import { Image } from '@/components/ui/image';
import NotFoundImage from '@/assets/images/404.svg';
import { sidebarAtom } from '@/components/ui/sidebar';

const UNAUTHORIZED_ERROR_CODE = 401;
const NOT_FOUND_ERROR_CODE = 404;

const UnauthorizedError = () => {
  const [, , removeCookie] = useCookies();
  const [, setSidebarOpen] = useAtom(sidebarAtom);
  return (
    <ErrorCard
      errorMessage="Your session has expired. Please sign in again."
      CTAText="Sign out"
      CTAAction={() => {
        removeCookie('accessToken', { path: APP_ROUTE.ROOT });
        localStorage.removeItem('workspace');
        localStorage.removeItem('user');
        localStorage.removeItem('sidebar');
        setSidebarOpen(true);
        window.location.reload();
      }}
    />
  );
};

const NotFoundError = () => {
  return (
    <div className="flex flex-col items-center justify-center h-screen gap-2 max-w-[426px] mx-auto">
      <div className="flex flex-col gap-10">
        <Image src={NotFoundImage} alt="404" width={180} height={750} />
        <p className="text-lg font-semibold text-grayscale-900">
          Sorry! Page not found.
        </p>
      </div>
      <div className="flex flex-col gap-6 items-center justify-center">
        <p className="text-sm text-grayscale-600 text-center">
          Seems like you&apos;re lost, but it ain&apos;t the end of the world.
          <br />
          Check back again shortly.
        </p>
        <Button asChild>
          <Link to="/">Go Home</Link>
        </Button>
      </div>
    </div>
  );
};

class ErrorBoundary extends Component<
  any,
  { hasError: boolean; errorCode: number | undefined }
> {
  constructor(props: any) {
    super(props);
    this.state = {
      hasError: false,
      errorCode: undefined,
    };
  }

  static getDerivedStateFromError(error: any): any {
    // Update state so the next render will show the fallback UI.
    const errorMessage = error?.data?.message || error?.message;
    if (errorMessage?.includes('Request failed with status code 404')) {
      return {
        hasError: true,
        errorCode: NOT_FOUND_ERROR_CODE,
      };
    }

    return {
      hasError: true,
      errorCode: error?.data?.code as number | undefined,
    };
  }

  render() {
    const { hasError, errorCode } = this.state;
    const { children } = this.props;
    if (hasError) {
      // if errorCode is 401, render the UnauthorizedError component
      if (errorCode === UNAUTHORIZED_ERROR_CODE) {
        return <UnauthorizedError />;
      }

      if (errorCode === NOT_FOUND_ERROR_CODE) {
        return <NotFoundError />;
      }

      // If error occurs, render the custom error page.
      const message =
        'Not to worry though, our engineers are staying up all night working on the fix. Feel free to send him some words of encouragement.';
      const { learnMoreClick, CTAText, CTAAction } = getErrorAction({
        message,
      });
      return (
        <ErrorCard
          errorMessage={message}
          learnMoreClick={learnMoreClick}
          CTAText={CTAText}
          CTAAction={CTAAction}
        />
      );
    }
    // Otherwise, Render children.
    return children;
  }
}

export { NotFoundError };
export default ErrorBoundary;
